import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpen, ArrowRight, Radio, Tv } from "lucide-react";
import { api } from "@/lib/api";

/**
 * "From the editor" strip — the single always-current spotlight at the top
 * of /feed and /rooms.
 *
 * It dynamically reflects whatever is front-and-center right now:
 *   1. LIVE NOW      — an active broadcast is streaming (red pulsing dot)
 *   2. NOW PLAYING   — the editor-featured archive broadcast (or newest one)
 *   3. LETTER (fallback) — this week's letter to the salon
 *
 * Aligned with the Landing "Now Playing" hero card so both surfaces show the
 * same spotlight simultaneously.
 *
 * Polls /live/status + /live/featured every 30s. Fails silently and falls
 * back to the letter link so the strip is never blocking.
 */
export const EditorsLetterStrip = () => {
  const [spotlight, setSpotlight] = useState(null); // { mode, to, kicker, title }

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        // Live state is handled globally by the sticky <LiveNowBanner /> that
        // is mounted on every member-facing page, so this strip only surfaces
        // NOW PLAYING (featured archive broadcast) or the LETTER fallback.
        // We intentionally do NOT fetch /live/status here to avoid a double
        // red band appearing on /feed and /rooms when Mimi goes live.
        const featuredRes = await api.get("/live/featured").catch(() => ({ data: null }));
        if (!active) return;
        const featured = featuredRes?.data;
        if (featured && featured.id) {
          setSpotlight({
            mode: "featured",
            to: `/broadcast/${featured.id}`,
            kicker: "Now playing",
            title: featured.title || "This week's featured broadcast.",
          });
        } else {
          setSpotlight(null); // fall back to the letter default
        }
      } catch {
        if (active) setSpotlight(null);
      }
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  // ── Choose mode-specific content ──────────────────────────────────────
  const isLive = spotlight?.mode === "live";
  const isFeatured = spotlight?.mode === "featured";
  const to = isLive || isFeatured ? spotlight.to : "/editors-letter";
  const kicker = isLive
    ? "Live now"
    : isFeatured
    ? "Now playing"
    : "From the editor";
  const title = isLive
    ? spotlight.title
    : isFeatured
    ? spotlight.title
    : "This week's letter to the salon.";
  const cta = isLive ? "Watch" : isFeatured ? "Play" : "Read";
  const Icon = isLive ? Radio : isFeatured ? Tv : BookOpen;
  const testid = isLive
    ? "editors-letter-strip-live"
    : isFeatured
    ? "editors-letter-strip-featured"
    : "editors-letter-strip";

  return (
    <Link
      to={to}
      data-testid={testid}
      data-mode={isLive ? "live" : isFeatured ? "featured" : "letter"}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "14px 24px",
        marginBottom: 32,
        border: "1px solid var(--border-default)",
        background: isLive
          ? "linear-gradient(90deg, rgba(211, 63, 63, 0.08) 0%, rgba(120, 34, 45, 0.04) 100%)"
          : "var(--bg-alt, rgba(120, 34, 45, 0.04))",
        textDecoration: "none",
        color: "inherit",
        flexWrap: "wrap",
        overflow: "hidden",
      }}
    >
      {/* Ensure the live-pulse keyframes are available even if this strip
          ever renders on a page without the Navbar. Duplicated intentionally. */}
      <style>{`
        @keyframes salonLivePulse {
          0%   { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0.65); }
          70%  { box-shadow: 0 0 0 8px rgba(211, 63, 63, 0); }
          100% { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0); }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
        <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
          <Icon
            size={18}
            strokeWidth={1.6}
            color={isLive ? "#D33F3F" : "var(--brand-accent)"}
          />
          {isLive && (
            <span
              aria-hidden="true"
              data-testid="editors-letter-strip-live-dot"
              style={{
                position: "absolute",
                top: -3,
                right: -4,
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#D33F3F",
                boxShadow: "0 0 0 0 rgba(211, 63, 63, 0.7)",
                animation: "salonLivePulse 1.6s ease-out infinite",
              }}
            />
          )}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            className="overline"
            style={{
              fontSize: 10,
              color: isLive ? "#D33F3F" : "var(--brand-accent)",
              letterSpacing: "0.22em",
              fontWeight: isLive ? 700 : 500,
            }}
          >
            {kicker}
          </div>
          <div
            className="font-serif"
            style={{
              fontSize: 15,
              fontStyle: "italic",
              color: "var(--text-primary)",
              lineHeight: 1.3,
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        </div>
      </div>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 500,
          color: isLive ? "#D33F3F" : "var(--brand-accent)",
          flexShrink: 0,
        }}
      >
        {cta} <ArrowRight size={12} strokeWidth={2} />
      </span>
    </Link>
  );
};

export default EditorsLetterStrip;
